import {ArrowRightIcon} from "@heroicons/react/20/solid";
import {MouseEvent, ReactNode, useId} from "react";

export type DirectoryItem = {
  id: string;
  title: string;
  description?: string;
  href?: string;
  onSelect?: () => void;
  icon?: ReactNode;
  status?: ReactNode;
  keywords?: string[];
  group?: string;
};

export type DirectoryItemCardProps = {
  item: DirectoryItem;
  onNavigate?: (href: string) => void;
};

const cardClassName = [
  "group flex min-h-28 w-full items-start gap-4 rounded-xl border border-[var(--ots-outline-variant)]",
  "bg-[var(--ots-surface)] p-4 text-left shadow-xs transition",
  "hover:-translate-y-0.5 hover:border-[var(--ots-outline)] hover:shadow-md",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]",
].join(" ");

const shouldNavigateInApp = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/** One destination or action in a directory grid. */
export const DirectoryItemCard = ({item, onNavigate}: DirectoryItemCardProps) => {
  const descriptionId = useId();
  const content = (
    <>
      {item.icon && (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--ots-primary-container)] text-[var(--ots-primary)] [&>svg]:size-5">
          {item.icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-[family-name:var(--ots-font-display)] text-sm font-semibold text-[var(--ots-secondary)]">
            {item.title}
          </span>
          {item.status}
        </span>
        {item.description && (
          <span id={descriptionId} className="mt-1.5 block text-xs leading-5 text-[var(--ots-text-muted)]">
            {item.description}
          </span>
        )}
      </span>
      <ArrowRightIcon
        aria-hidden="true"
        className="mt-1 size-4 shrink-0 text-[var(--ots-text-muted)] transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--ots-primary)]"/>
    </>
  );

  const href = item.href;
  if (href) {
    return (
      <a
        href={href}
        aria-describedby={item.description ? descriptionId : undefined}
        className={cardClassName}
        onClick={(event) => {
          if (onNavigate && shouldNavigateInApp(event)) {
            event.preventDefault();
            onNavigate(href);
          }
        }}>
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      aria-describedby={item.description ? descriptionId : undefined}
      className={cardClassName}
      onClick={item.onSelect}>
      {content}
    </button>
  );
};
